// ============================================================================
// Expression evaluator — used by show_if and validation rule expressions
// ============================================================================
// Small, safe boolean-expression interpreter. Deliberately limited in scope:
//
// SUPPORTED
//   - Operands: bare identifiers (resolved to form values), single-quoted
//     string literals, numeric literals, true / false / null
//   - Comparison: == != > >= < <=
//   - Logical: && || !
//   - Parentheses for grouping
//   - Member access on grid rows (read-only): `bidders.length`, `bidders[0].rank`
//     — for now only `<field>.length` is supported (count of grid rows)
//
// NOT SUPPORTED
//   - Arithmetic (+ - * /) — not needed for show_if / validation use cases
//   - Function calls — keeps surface tiny + safe
//   - String methods
//   - eval / new Function — never used; we tokenize + recurse
//
// On parse error: returns FALSE and logs a console warning. Show-if conditions
// degrade gracefully (field stays visible); validations degrade by passing.

export type EvalContext = Record<string, unknown>;

interface Token {
  kind: 'IDENT' | 'STRING' | 'NUMBER' | 'BOOL' | 'NULL' | 'OP' | 'LPAREN' | 'RPAREN' | 'DOT' | 'LBRACKET' | 'RBRACKET';
  value: string;
}

// ----------------------------------------------------------------------------
// Tokenizer
// ----------------------------------------------------------------------------

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = src.length;
  while (i < n) {
    const c = src[i];
    // Whitespace
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    // String literal — single-quoted, no escapes
    if (c === '\'') {
      let end = i + 1;
      while (end < n && src[end] !== '\'') end++;
      if (end >= n) throw new Error('Unterminated string literal');
      tokens.push({ kind: 'STRING', value: src.slice(i + 1, end) });
      i = end + 1;
      continue;
    }
    // Number
    if (c >= '0' && c <= '9') {
      let end = i;
      while (end < n && ((src[end] >= '0' && src[end] <= '9') || src[end] === '.')) end++;
      tokens.push({ kind: 'NUMBER', value: src.slice(i, end) });
      i = end;
      continue;
    }
    // Identifier / keyword
    if ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_') {
      let end = i;
      while (end < n && ((src[end] >= 'a' && src[end] <= 'z') || (src[end] >= 'A' && src[end] <= 'Z') ||
                          (src[end] >= '0' && src[end] <= '9') || src[end] === '_')) end++;
      const ident = src.slice(i, end);
      if (ident === 'true' || ident === 'false') tokens.push({ kind: 'BOOL', value: ident });
      else if (ident === 'null') tokens.push({ kind: 'NULL', value: 'null' });
      else tokens.push({ kind: 'IDENT', value: ident });
      i = end;
      continue;
    }
    // Two-char operators
    if (i + 1 < n) {
      const two = src.slice(i, i + 2);
      if (two === '==' || two === '!=' || two === '>=' || two === '<=' || two === '&&' || two === '||') {
        tokens.push({ kind: 'OP', value: two });
        i += 2;
        continue;
      }
    }
    // One-char operators / structure
    if (c === '>' || c === '<' || c === '!') { tokens.push({ kind: 'OP', value: c }); i++; continue; }
    if (c === '(') { tokens.push({ kind: 'LPAREN', value: '(' }); i++; continue; }
    if (c === ')') { tokens.push({ kind: 'RPAREN', value: ')' }); i++; continue; }
    if (c === '.') { tokens.push({ kind: 'DOT', value: '.' }); i++; continue; }
    if (c === '[') { tokens.push({ kind: 'LBRACKET', value: '[' }); i++; continue; }
    if (c === ']') { tokens.push({ kind: 'RBRACKET', value: ']' }); i++; continue; }
    throw new Error(`Unexpected character at ${i}: ${c}`);
  }
  return tokens;
}

// ----------------------------------------------------------------------------
// Parser — recursive-descent. Precedence: || < && < comparison < unary !
// ----------------------------------------------------------------------------

class Parser {
  private idx = 0;
  constructor(private tokens: Token[]) {}

  private peek(): Token | undefined { return this.tokens[this.idx]; }
  private consume(): Token { return this.tokens[this.idx++]; }

  private match(kind: Token['kind'], value?: string): boolean {
    const t = this.peek();
    if (!t) return false;
    if (t.kind !== kind) return false;
    if (value !== undefined && t.value !== value) return false;
    return true;
  }

  parseOr(ctx: EvalContext): boolean {
    let left = this.parseAnd(ctx);
    while (this.match('OP', '||')) {
      this.consume();
      const right = this.parseAnd(ctx);
      left = !!left || !!right;
    }
    return !!left;
  }

  private parseAnd(ctx: EvalContext): boolean {
    let left: any = this.parseNot(ctx);
    while (this.match('OP', '&&')) {
      this.consume();
      const right = this.parseNot(ctx);
      left = !!left && !!right;
    }
    return !!left;
  }

  private parseNot(ctx: EvalContext): any {
    if (this.match('OP', '!')) {
      this.consume();
      return !this.parseNot(ctx);
    }
    return this.parseComparison(ctx);
  }

  private parseComparison(ctx: EvalContext): any {
    const left = this.parsePrimary(ctx);
    const t = this.peek();
    if (t && t.kind === 'OP' && ['==','!=','>','>=','<','<='].includes(t.value)) {
      this.consume();
      const right = this.parsePrimary(ctx);
      switch (t.value) {
        case '==': return left == right; // eslint-disable-line eqeqeq
        case '!=': return left != right; // eslint-disable-line eqeqeq
        case '>':  return (left as any) > (right as any);
        case '>=': return (left as any) >= (right as any);
        case '<':  return (left as any) < (right as any);
        case '<=': return (left as any) <= (right as any);
      }
    }
    return left;
  }

  private parsePrimary(ctx: EvalContext): any {
    const t = this.consume();
    if (!t) throw new Error('Unexpected end of expression');
    switch (t.kind) {
      case 'LPAREN': {
        const v = this.parseOr(ctx);
        if (!this.match('RPAREN')) throw new Error('Expected )');
        this.consume();
        return v;
      }
      case 'STRING': return t.value;
      case 'NUMBER': return Number(t.value);
      case 'BOOL':   return t.value === 'true';
      case 'NULL':   return null;
      case 'IDENT':  return this.resolveIdent(t.value, ctx);
      default: throw new Error(`Unexpected token ${t.kind} ${t.value}`);
    }
  }

  private resolveIdent(name: string, ctx: EvalContext): unknown {
    let value: unknown = ctx[name];
    // Member access — only `.length` and numeric subscripts supported.
    while (this.match('DOT') || this.match('LBRACKET')) {
      if (this.match('DOT')) {
        this.consume();
        const member = this.consume();
        if (member.kind !== 'IDENT') throw new Error('Expected identifier after .');
        if (member.value === 'length' && Array.isArray(value)) {
          value = value.length;
        } else if (value && typeof value === 'object') {
          value = (value as Record<string, unknown>)[member.value];
        } else {
          value = undefined;
        }
      } else if (this.match('LBRACKET')) {
        this.consume();
        const idxTok = this.consume();
        if (idxTok.kind !== 'NUMBER') throw new Error('Expected numeric index');
        if (!this.match('RBRACKET')) throw new Error('Expected ]');
        this.consume();
        if (Array.isArray(value)) value = value[Number(idxTok.value)];
        else value = undefined;
      }
    }
    return value;
  }
}

// ----------------------------------------------------------------------------
// Public entrypoint
// ----------------------------------------------------------------------------

/**
 * Evaluate a boolean expression against `values`. Returns the parsed result
 * coerced to boolean for show_if / validation use. On parse error, logs a
 * warning and returns `defaultOnError` (TRUE for show_if so the field stays
 * visible; TRUE for validations so save isn't blocked by a malformed rule).
 */
export function evalBool(expr: string, values: EvalContext, defaultOnError = true): boolean {
  try {
    const tokens = tokenize(expr);
    if (tokens.length === 0) return defaultOnError;
    const parser = new Parser(tokens);
    return !!parser.parseOr(values);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[expressionEvaluator] failed to evaluate', JSON.stringify(expr), err);
    return defaultOnError;
  }
}
